import { createContext, useEffect, useReducer, useCallback } from "react";

// Session config (in milliseconds)
const SESSION_CONFIG = {
  INACTIVITY_TIMEOUT: 30 * 60 * 1000, // 30 minutes of inactivity
  SESSION_DURATION: 8 * 60 * 60 * 1000, // 8 hours max session
};

// Get initial state from sessionStorage (cleared on browser close)
const getInitialState = () => {
  try {
    const savedSession = sessionStorage.getItem("authSession");
    if (savedSession) {
      const session = JSON.parse(savedSession);
      const now = Date.now();
      
      // Check if session is still valid
      if (session.sessionExpiry && now < session.sessionExpiry) {
        const inactivityTime = now - session.lastActivity;
        if (inactivityTime < SESSION_CONFIG.INACTIVITY_TIMEOUT) {
          return session; // Session still valid!
        }
      }
    }
  } catch (error) {
    console.error("Error reading session:", error);
  }
  
  // Return empty state if no valid session
  return {
    user: null,
    loading: false,
    error: null,
    sessionExpiry: null,
    lastActivity: Date.now(),
  };
};

const INITIAL_STATE = getInitialState();

export const AuthContext = createContext(INITIAL_STATE);

const AuthReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_START":
      return {
        ...state,
        user: null,
        loading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
      const now = Date.now();
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
        sessionExpiry: now + SESSION_CONFIG.SESSION_DURATION,
        lastActivity: now,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        user: null,
        loading: false,
        error: action.payload,
        sessionExpiry: null,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        loading: false,
        error: null,
        sessionExpiry: null,
        lastActivity: null,
      };
    case "UPDATE_ACTIVITY":
      return {
        ...state,
        lastActivity: Date.now(),
      };
    case "SESSION_EXPIRED":
      return {
        ...state,
        user: null,
        sessionExpiry: null,
        error: { message: "Session expired. Please login again." },
      };
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);

  // Save session to sessionStorage (cleared on browser close)
  useEffect(() => {
    if (state.user) {
      const sessionData = {
        user: state.user,
        sessionExpiry: state.sessionExpiry,
        lastActivity: state.lastActivity,
      };
      sessionStorage.setItem("authSession", JSON.stringify(sessionData));
    } else {
      sessionStorage.removeItem("authSession");
    }
  }, [state.user, state.sessionExpiry, state.lastActivity]);

  // Track user activity - update on any interaction
  useEffect(() => {
    const trackActivity = () => {
      if (state.user) {
        dispatch({ type: "UPDATE_ACTIVITY" });
      }
    };

    // Track various user interactions
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    events.forEach((event) => {
      window.addEventListener(event, trackActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, [state.user]);

  // Session expiry checker - runs every 60 seconds
  useEffect(() => {
    if (!state.user) return;

    const sessionChecker = setInterval(() => {
      const now = Date.now();

      // Check if max session duration exceeded
      if (state.sessionExpiry && now > state.sessionExpiry) {
        dispatch({ type: "SESSION_EXPIRED" });
        return;
      }

      // Check if user is inactive
      if (state.lastActivity) {
        const inactivityTime = now - state.lastActivity;
        if (inactivityTime > SESSION_CONFIG.INACTIVITY_TIMEOUT) {
          dispatch({ type: "SESSION_EXPIRED" });
        }
      }
    }, 60000); // Check every 60 seconds

    return () => clearInterval(sessionChecker);
  }, [state.user, state.sessionExpiry, state.lastActivity]);

  // Logout function
  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
  }, []);

  // Get remaining session time (in minutes)
  const getSessionTimeRemaining = useCallback(() => {
    if (!state.user || !state.lastActivity) return null;

    const now = Date.now();
    const inactivityTime = now - state.lastActivity;
    const timeLeft = SESSION_CONFIG.INACTIVITY_TIMEOUT - inactivityTime;

    return Math.ceil(timeLeft / 60000); // Convert to minutes
  }, [state.user, state.lastActivity]);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        loading: state.loading,
        error: state.error,
        dispatch,
        logout,
        sessionExpiry: state.sessionExpiry,
        lastActivity: state.lastActivity,
        getSessionTimeRemaining,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};